import { getConnectorAvatar, isConnectorId, } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { updateSearchParams } from '#pplx/frontend/apps/ask/routing/historySearchParams.js';
import { isPassivelyConnectedConnector, isSourceConnected, } from '#pplx/frontend/apps/ask/sources/hooks/sourceStatusPredicates.js';
const NAME_TYPE_MAPPING = {
    factset: 'FACTSET',
    crunchbase: 'UNKNOWN',
    google_drive: 'GOOGLE_DRIVE',
    gcal: 'GCAL',
    onedrive: 'ONEDRIVE',
    sharepoint: 'SHAREPOINT',
    wiley: 'WILEY', // deprecated
    dropbox: 'DROPBOX',
    box: 'BOX',
    edgar: 'UNKNOWN', // deprecated
    outlook: 'OUTLOOK',
    linear: 'LINEAR',
    linear_alt: 'LINEAR_ALT',
    notion_mcp: 'NOTION_MCP',
    slack: 'SLACK',
    github_mcp_direct: 'GITHUB_MCP_DIRECT',
    asana_mcp_direct: 'ASANA_MCP_DIRECT',
    asana_mcp_merge: 'ASANA_MCP_MERGE',
    atlassian_mcp_direct: 'ATLASSIAN_MCP_DIRECT',
    slack_direct: 'SLACK_DIRECT',
    jira_mcp_merge: 'JIRA_MCP_MERGE',
    confluence_mcp_merge: 'CONFLUENCE_MCP_MERGE',
    microsoft_teams_mcp_merge: 'MICROSOFT_TEAMS_MCP_MERGE',
    wiley_mcp_cashmere: 'WILEY',
    cbinsights_mcp_cashmere: 'UNKNOWN',
    pitchbook_mcp_cashmere: 'UNKNOWN',
    statista_mcp_cashmere: 'UNKNOWN',
    zoom: 'ZOOM',
    apple_healthkit: 'APPLE_HEALTHKIT',
    medical_records: 'MEDICAL_RECORDS',
    wearables: 'WEARABLES',
    plaid: 'PLAID',
    function_health: 'FUNCTION_HEALTH',
    // Retained only so historical connector records remain parseable.
    openrouter: 'UNKNOWN',
};
export function connectionTypeForName(name) {
    return NAME_TYPE_MAPPING[name];
}
export function fileUploadSourceTypeForConnector(connectorId) {
    switch (connectorId) {
        case 'google_drive':
        case 'dropbox':
        case 'sharepoint':
            return connectorId;
        default:
            return 'other_connectors';
    }
}
export function isConnectorFilePickerReady(connector) {
    switch (connector?.name) {
        case 'google_drive':
        case 'onedrive':
        case 'sharepoint':
        case 'dropbox':
            return connector.picker_credentials?.client_id != null;
        case 'box':
            return connector.picker_credentials?.api_key != null;
        default:
            return false;
    }
}
export function isAlwaysConnectedSource(source) {
    return (source?.auth_type === 'none' &&
        source.auth_scope === 'system' &&
        source.source_scope === 'global');
}
// Plaid accounts are managed from the finance surface, not by a connector disconnect.
const FINANCE_MANAGED_SOURCE_ID = 'plaid';
export function isSourceOrgScoped(source) {
    return source.auth_scope === 'organization';
}
export function canDisconnectSource(source) {
    return source.id !== FINANCE_MANAGED_SOURCE_ID && !isAlwaysConnectedSource(source);
}
/**
 * The viewer's own accounts on a source. An organization-scoped source lists the organization's
 * shared connection, which the viewer must not disconnect; local MCP sources list none.
 */
export function getSourceConnectionUUIDs(source) {
    return 'user_connections' in source && !isSourceOrgScoped(source)
        ? (source.user_connections ?? []).map((connection) => connection.connection_uuid)
        : [];
}
export function isUserInstalledSource(source) {
    return ((source.auth_type === 'oauth' || source.auth_type === 'api_key') &&
        source.auth_scope !== 'system' &&
        source.is_premium !== true);
}
export function resolveSourceAvatarUrl(source) {
    const sourceIconUrl = source.icon_url?.trim();
    if (sourceIconUrl)
        return sourceIconUrl;
    const familyIconUrl = source.family_info?.family_icon_url?.trim();
    if (familyIconUrl)
        return familyIconUrl;
    return isConnectorId(source.id) ? getConnectorAvatar(source.id) : null;
}
function getSourceLike(source) {
    return source != null && typeof source === 'object' ? source : null;
}
export function isPremiumSource(source) {
    return getSourceLike(source)?.is_premium === true;
}
function getFamilyMembers(source) {
    const familyInfo = getSourceLike(source)?.family_info;
    return Array.isArray(familyInfo?.members) ? familyInfo.members : [];
}
// Passively connected sources run on a Perplexity-held key, so the backend `connected` filter
// drops them even though they are usable without setup.
export function isSourceIncludedWithComputer(source) {
    return (isAlwaysConnectedSource(source) ||
        isSourcePremiumOrInPremiumFamily(source) ||
        (source?.auth_scope === 'system' &&
            isPassivelyConnectedConnector(source) &&
            isSourceConnected(source)));
}
export function isSourcePremiumOrInPremiumFamily(source) {
    return isPremiumSource(source) || getFamilyMembers(source).some(isPremiumSource);
}
const CONNECTOR_PARAM_KEY = 'connector';
/**
 * Extracts connection type fron url if valid
 *
 * url?connector=DROPBOX => "DROPBOX"
 * url?connector=MYSTERY => null
 */
export function connectionTypeFromUrlParams(params, connectionTypes) {
    const value = params.get(CONNECTOR_PARAM_KEY);
    const isValid = connectionTypes.includes(value);
    if (isValid) {
        return value;
    }
    return null;
}
/**
 * Sets connection type url param
 * "DROPBOX" => url?connector=DROPBOX
 */
export function setConnectionTypeUrlParam(connectionType) {
    updateSearchParams({ [CONNECTOR_PARAM_KEY]: connectionType ?? undefined });
}
//# sourceMappingURL=connectors.js.map